import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, ChevronRight, ChevronLeft, Trash2, Flag, Pencil, Plus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { AbcNotation } from "../components/AbcNotation";
import { TuneTypeBadge, typeStyle } from "../components/TuneTypeBadge";
import { FilterCarousel } from "../components/FilterCarousel";
import { getFavourites, removeFavourite } from "../lib/favourites";
import { getFlags, removeFlag, getLabels, setLabel, getPalette, flagHex, createFlagColor } from "../lib/flags";
import { firstLineAbc } from "../lib/abcUtils";
import { readView, saveView, restoreScroll } from "../lib/scrollMemory";

const VIEW_KEY = "foinn_fav_view";

const PREVIEW_PARAMS = { preferredMeasuresPerLine: 4 };
const plural = (t) => (t.endsWith("s") ? t : `${t}s`);

const Pill = ({ active, onClick, children, testid, color, textColor }) => (
  <button
    onClick={onClick}
    className="shrink-0 whitespace-nowrap rounded-full border-2 px-4 py-2 text-sm font-extrabold capitalize transition-transform hover:brightness-95 active:scale-95"
    style={{
      borderColor: color || (active ? "#2d2824" : "#e5e0d8"),
      backgroundColor: active ? color || "#2d2824" : "#fff",
      color: active ? textColor || "#fff" : "#2d2824",
    }}
    data-testid={testid}
  >
    {children}
  </button>
);

const ItemCard = ({ item, index, onOpen, onRemove, flagColor }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onOpen(item)}
    onKeyDown={(e) => e.key === "Enter" && onOpen(item)}
    className="group float-up cursor-pointer rounded-3xl border border-[#e5e0d8] bg-[#fffdf9] p-5 shadow-[0_8px_30px_rgba(45,40,36,0.05)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(45,40,36,0.10)]"
    style={{ animationDelay: `${Math.min(index * 45, 400)}ms` }}
    data-testid={flagColor ? "flag-card" : "favourite-card"}
    data-flag-color={flagColor || undefined}
  >
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-serif text-2xl font-bold leading-tight text-[#2d2824]" data-testid="favourite-title">
        {item.name}
      </h3>
      <div className="flex shrink-0 items-center gap-2">
        {flagColor && (
          <Flag className="h-5 w-5" style={{ color: flagHex(flagColor), fill: flagHex(flagColor) }} />
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.key);
          }}
          className="rounded-full border border-[#e5e0d8] bg-white p-1.5 text-[#a89e92] transition-colors hover:text-[#e11d48]"
          data-testid="favourite-remove"
          aria-label="remove"
        >
          <Trash2 className="h-4 w-4" />
        </button>
        <ChevronRight className="h-6 w-6 text-[#a89e92] transition-transform duration-200 group-hover:translate-x-1" />
      </div>
    </div>

    <div className="mt-2 flex flex-wrap items-center gap-2">
      <TuneTypeBadge type={item.type} />
      <span className="text-[11px] font-bold text-[#6e655b]">Version {item.version}</span>
      {item.edited && (
        <span className="rounded-full bg-[#ff6b00] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white">
          Edited
        </span>
      )}
    </div>

    <div className="mt-4 overflow-hidden rounded-2xl bg-[#fbf9f4] px-3 py-3">
      {item.abc ? (
        <AbcNotation abc={firstLineAbc(item.abc)} params={PREVIEW_PARAMS} />
      ) : (
        <span className="text-sm font-semibold text-[#6e655b]">Open to view the notation</span>
      )}
    </div>
  </div>
);

export default function FavouritesPage() {
  const navigate = useNavigate();
  const view = readView(VIEW_KEY) || {};
  const [tab, setTab] = useState(view.tab || "favourites");
  const [favs, setFavs] = useState([]);
  const [flags, setFlags] = useState([]);
  const [labels, setLabels] = useState(getLabels());
  const [group, setGroup] = useState(view.group || "__all__");
  const [colorFilter, setColorFilter] = useState(view.colorFilter || "__all__");
  const [flagGroup, setFlagGroup] = useState(view.flagGroup || "__all__");
  const [renaming, setRenaming] = useState(null);
  const { user } = useAuth();
  const premium = !!(user?.unlimited_searches || user?.is_admin);

  // come back from a tune exactly where we left off
  useEffect(() => restoreScroll(view.y), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const update = () => {
      setFavs(getFavourites());
      setFlags(getFlags());
      setLabels(getLabels());
    };
    update();
    window.addEventListener("fav-changed", update);
    window.addEventListener("flag-changed", update);
    return () => {
      window.removeEventListener("fav-changed", update);
      window.removeEventListener("flag-changed", update);
    };
  }, []);

  const groups = useMemo(() => {
    const counts = {};
    favs.forEach((f) => {
      const t = f.type || "other";
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [favs]);

  const flagGroups = useMemo(() => {
    const counts = {};
    flags.forEach((f) => {
      const t = f.type || "other";
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [flags]);

  const palette = useMemo(() => getPalette(), [flags, labels]);

  const colorCounts = useMemo(() => {
    const c = {};
    flags.forEach((f) => {
      c[f.color] = (c[f.color] || 0) + 1;
    });
    return c;
  }, [flags]);

  // always carry the snapshot key so the saved version (and any edit) reopens
  const open = (f) => {
    saveView(VIEW_KEY, { tab, group, colorFilter, flagGroup });
    navigate(`/tune/${f.tune_id}?fav=${encodeURIComponent(f.key)}`);
  };

  const shownFavs = group === "__all__" ? favs : favs.filter((f) => (f.type || "other") === group);
  const shownFlags = flags.filter(
    (f) =>
      (colorFilter === "__all__" || f.color === colorFilter) &&
      (flagGroup === "__all__" || (f.type || "other") === flagGroup)
  );
  const list = tab === "favourites" ? shownFavs : shownFlags;

  return (
    <div className="min-h-screen" data-testid="favourites-page">
      <AppHeader />

      <section className="mx-auto max-w-3xl px-5 pt-8">
        <button
          onClick={() => navigate("/")}
          className="mb-4 flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-3 py-2 text-sm font-bold text-[#2d2824] transition-colors hover:bg-[#f3efea]"
          data-testid="favourites-back"
        >
          <ChevronLeft className="h-4 w-4" /> Back to search
        </button>

        {/* tabs */}
        <div className="flex gap-2" data-testid="favourites-tabs">
          <Pill
            active={tab === "favourites"}
            onClick={() => setTab("favourites")}
            color={tab === "favourites" ? "#4CAF50" : undefined}
            testid="tab-favourites"
          >
            <span className="flex items-center gap-1.5">
              <Star className="h-4 w-4" style={{ fill: tab === "favourites" ? "#fff" : "#4CAF50", color: tab === "favourites" ? "#fff" : "#4CAF50" }} />
              Favourites · {favs.length}
            </span>
          </Pill>
          <Pill active={tab === "flags"} onClick={() => setTab("flags")} testid="tab-flags">
            <span className="flex items-center gap-1.5">
              <Flag className="h-4 w-4" />
              Flags · {flags.length}
            </span>
          </Pill>
        </div>

        {tab === "favourites" ? (
          groups.length > 1 && (
            <div className="mt-5" data-testid="favourite-groups">
              <FilterCarousel testid="favourite-type-carousel">
                <Pill
                  active={group === "__all__"}
                  onClick={() => setGroup("__all__")}
                  testid="favourite-group-all"
                >
                  All · {favs.length}
                </Pill>
                {groups.map(([t, n]) => {
                  const st = typeStyle(t);
                  return (
                    <Pill
                      key={t}
                      active={group === t}
                      onClick={() => setGroup(t)}
                      color={st.bg}
                      textColor={st.text}
                      testid={`favourite-group-${t.replace(/\s+/g, "-")}`}
                    >
                      {plural(t)} · {n}
                    </Pill>
                  );
                })}
              </FilterCarousel>
            </div>
          )
        ) : (
          <div className="mt-5 space-y-1" data-testid="flag-filters">
            <FilterCarousel testid="flag-colour-carousel">
              <Pill
                active={colorFilter === "__all__"}
                onClick={() => setColorFilter("__all__")}
                testid="flag-filter-all"
              >
                All · {flags.length}
              </Pill>
              {palette.map((c) => (
                <span key={c} className="flex shrink-0 items-center gap-1">
                  <Pill
                    active={colorFilter === c}
                    onClick={() => setColorFilter(c)}
                    color={flagHex(c)}
                    testid={`flag-filter-${c}`}
                  >
                    <span className="flex items-center gap-1.5">
                      <Flag
                        className="h-4 w-4"
                        style={{
                          color: colorFilter === c ? "#fff" : flagHex(c),
                          fill: colorFilter === c ? "#fff" : flagHex(c),
                        }}
                      />
                      {labels[c] || c} · {colorCounts[c] || 0}
                    </span>
                  </Pill>
                  <button
                    onClick={() => setRenaming(c)}
                    className="shrink-0 rounded-full border border-[#e5e0d8] bg-white p-1.5 text-[#a89e92] hover:text-[#2d2824]"
                    data-testid={`flag-rename-${c}`}
                    aria-label={`rename ${c} flag`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
              {premium && (
                <button
                  onClick={() => {
                    const c = createFlagColor();
                    if (!c) return;
                    setRenaming(c);
                    setTimeout(() => {
                      const pill = document.querySelector(`[data-testid="flag-filter-${c}"]`);
                      const row = pill?.closest("[data-carousel-track]") || pill?.parentElement?.parentElement;
                      if (row) row.scrollLeft = row.scrollWidth;
                    }, 50);
                  }}
                  className="flex shrink-0 items-center gap-1 rounded-full border-2 border-dashed border-[#c9c1b5] bg-white px-3.5 py-1.5 text-sm font-bold text-[#6e655b] transition-colors hover:border-[#2d2824] hover:text-[#2d2824]"
                  data-testid="flag-create-button"
                >
                  <Plus className="h-4 w-4" /> New flag
                </button>
              )}
            </FilterCarousel>

            {flagGroups.length > 1 && (
              <FilterCarousel testid="flag-type-carousel">
                <Pill
                  active={flagGroup === "__all__"}
                  onClick={() => setFlagGroup("__all__")}
                  testid="flag-group-all"
                >
                  All types · {flags.length}
                </Pill>
                {flagGroups.map(([t, n]) => {
                  const st = typeStyle(t);
                  return (
                    <Pill
                      key={t}
                      active={flagGroup === t}
                      onClick={() => setFlagGroup(t)}
                      color={st.bg}
                      textColor={st.text}
                      testid={`flag-group-${t.replace(/\s+/g, "-")}`}
                    >
                      {plural(t)} · {n}
                    </Pill>
                  );
                })}
              </FilterCarousel>
            )}

            {renaming && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setLabel(renaming, e.target.elements.label.value.trim() || labels[renaming]);
                  setRenaming(null);
                }}
                className="mt-3 flex items-center gap-2"
              >
                <input
                  name="label"
                  defaultValue={labels[renaming]}
                  maxLength={40}
                  autoFocus
                  className="flex-1 rounded-xl border-2 px-3 py-2 text-sm font-bold"
                  style={{ borderColor: flagHex(renaming) }}
                  data-testid="flag-rename-input"
                />
                <button
                  type="submit"
                  className="rounded-xl px-4 py-2 text-sm font-bold text-white"
                  style={{ backgroundColor: flagHex(renaming) }}
                  data-testid="flag-rename-save"
                >
                  Save
                </button>
              </form>
            )}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-24 pt-8">
        {list.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            {tab === "favourites" ? (
              <Star className="h-12 w-12 text-[#c9c1b5]" />
            ) : (
              <Flag className="h-12 w-12 text-[#c9c1b5]" />
            )}
            <p className="font-serif text-2xl font-bold text-[#2d2824]">
              {tab === "favourites" ? "No favourites yet" : "No flagged tunes yet"}
            </p>
            <p className="max-w-xs text-sm text-[#6e655b]">
              {tab === "favourites"
                ? "Tap the star on any tune to keep it here."
                : "Use the Flag button on a tune to colour-code your practice list."}
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-2 rounded-full bg-[#2d2824] px-6 py-3 text-sm font-extrabold text-white"
              data-testid="favourites-empty-search"
            >
              Find your tune
            </button>
          </div>
        ) : (
          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-5">
            {list.map((f, i) => (
              <ItemCard
                key={f.key}
                item={f}
                index={i}
                onOpen={open}
                onRemove={tab === "favourites" ? removeFavourite : removeFlag}
                flagColor={tab === "flags" ? f.color : null}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
